(() => {
  const selector = '[data-dizzign-video-hero]';

  function initialiseVideoHero(hero) {
    if (hero.dataset.videoHeroInitialised === 'true') return;

    hero.dataset.videoHeroInitialised = 'true';
    const youtubeId = hero.dataset.youtubeId;
    const videoSource = hero.dataset.videoSource;
    const toggles = [...hero.querySelectorAll('[data-video-toggle]')];
    const mount = hero.querySelector('[data-video-mount]');
    // Keep the template fallback briefly for visitors served Shopify's cached
    // pre-update section HTML. New markup renders the video directly.
    const uploadedVideoTemplate = hero.querySelector('[data-uploaded-video-template]');
    if (!toggles.length || !mount || (videoSource === 'youtube' && !youtubeId) || (videoSource === 'uploaded' && !mount.querySelector('video') && !uploadedVideoTemplate)) return;

    let player;

    const setLoading = (loading) => {
      hero.classList.toggle('is-loading', loading);
      hero.setAttribute('aria-busy', String(loading));
      const playToggle = hero.querySelector('.dizzign-video-hero__toggle--play');
      if (playToggle) {
        playToggle.disabled = loading;
        playToggle.setAttribute('aria-label', loading ? 'Loading film' : hero.dataset.playLabel);
      }
    };

    const setState = (playing) => {
      setLoading(false);
      hero.classList.toggle('is-playing', playing);
      hero.querySelector('.dizzign-video-hero__video-overlay')?.setAttribute('aria-hidden', String(!playing));
      toggles.forEach((toggle) => {
        toggle.setAttribute('aria-pressed', String(playing));
        toggle.setAttribute('aria-label', playing ? hero.dataset.pauseLabel : hero.dataset.playLabel);
        const label = toggle.querySelector('[data-video-label]');
        if (label) label.textContent = playing ? hero.dataset.pauseLabel : hero.dataset.playLabel;
      });
    };

    const createYouTubePlayer = () => {
      if (player) return player;
      player = document.createElement('iframe');
      player.title = hero.dataset.iframeTitle;
      player.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture';
      player.allowFullscreen = true;
      player.loading = 'eager';
      player.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(youtubeId)}?autoplay=1&enablejsapi=1&rel=0&modestbranding=1&playsinline=1`;
      mount.append(player);
      return player;
    };

    const createUploadedPlayer = () => {
      if (player) return player;
      player = mount.querySelector('video');
      if (!player && uploadedVideoTemplate) {
        mount.append(uploadedVideoTemplate.content.cloneNode(true));
        player = mount.querySelector('video');
      }
      if (player) {
        player.tabIndex = -1;
        // `playsinline` is required for iPhone and iPad. The prefixed form
        // also covers older WebKit versions still found on client devices.
        player.setAttribute('playsinline', '');
        player.setAttribute('webkit-playsinline', '');
        player.addEventListener('playing', () => {
          player.tabIndex = 0;
          setState(true);
        });
        player.addEventListener('error', () => setState(false));
      }
      return player;
    };

    // Shopify-hosted files need more than metadata on mobile Safari. As the
    // hero approaches, buffer the film in the background so a real first tap
    // can start it immediately. This does not autoplay and runs only for a
    // visitor who reaches the hero.
    const prepareUploadedPlayer = () => {
      const uploadedPlayer = createUploadedPlayer();
      if (!uploadedPlayer) return;
      uploadedPlayer.preload = 'auto';
      uploadedPlayer.load();
    };

    const playUploadedVideo = () => {
      const uploadedPlayer = createUploadedPlayer();
      if (!uploadedPlayer) return;

      uploadedPlayer.preload = 'auto';
      setLoading(true);

      // Keep this play() request directly inside the tap handler. Moving it
      // to a later load/canplay event loses iOS's user-activation permission.
      const playRequest = uploadedPlayer.play();
      if (!playRequest || typeof playRequest.then !== 'function') {
        setState(!uploadedPlayer.paused);
        return;
      }

      playRequest
        .then(() => {
          uploadedPlayer.tabIndex = 0;
          setState(true);
        })
        .catch(() => setState(false));
    };

    if (videoSource === 'uploaded') {
      if ('IntersectionObserver' in window) {
        const mediaPreloadObserver = new IntersectionObserver((entries, observer) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          prepareUploadedPlayer();
          observer.disconnect();
        }, { rootMargin: '400px 0px' });
        mediaPreloadObserver.observe(hero);
      } else {
        prepareUploadedPlayer();
      }
    }

    const sendCommand = (command) => {
      if (!player || !player.contentWindow) return;
      player.contentWindow.postMessage(JSON.stringify({ event: 'command', func: command, args: '' }), 'https://www.youtube-nocookie.com');
    };

    toggles.forEach((toggle) => toggle.addEventListener('click', () => {
      const playing = !hero.classList.contains('is-playing');
      if (playing) {
        if (videoSource === 'uploaded') {
          playUploadedVideo();
          return;
        }

        createYouTubePlayer();
        setState(true);
        if (player.dataset.loaded === 'true') sendCommand('playVideo');
        player.addEventListener('load', () => {
          player.dataset.loaded = 'true';
          if (hero.classList.contains('is-playing')) sendCommand('playVideo');
        }, { once: true });
      } else {
        if (videoSource === 'uploaded') {
          player?.pause();
          if (player) player.tabIndex = -1;
        }
        else sendCommand('pauseVideo');
        setState(false);
      }
    }));
  }

  function initialiseAll(scope = document) {
    scope.querySelectorAll(selector).forEach(initialiseVideoHero);
  }

  document.addEventListener('DOMContentLoaded', () => initialiseAll());
  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();
